import React, { useCallback, useEffect, useState } from 'react'
import Typography from '@mui/material/Typography'
import { Chip, Table, TableBody, TableCell, TableHead, TableRow, Skeleton } from '@mui/material'
import Dialog from '../../../../../dialog/Dialog'
import ButtonStartIcon from '../../../../../button/ButtonStartIcon'
import useThirdPartyAccess from '../../../../../../hooks/setting/useThirdPartyAccess'

const STATUS_CHIP = {
  running: { label: 'Running', color: 'warning' },
  ok: { label: 'OK', color: 'success' },
  error: { label: 'Error', color: 'error' }
}

const formatDate = (iso) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-AU', {
    dateStyle: 'medium',
    timeStyle: 'short'
  })
}

const triggeredByLabel = (run) => {
  const by = run?.triggeredBy
  if (!by) return '—'
  return by.fullName || by.name || by.code || '—'
}

const HistoryDialog = ({ show, onClose, credential = {} }) => {
  const { getHistory } = useThirdPartyAccess()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    if (!credential?._id) return
    setLoading(true)
    try {
      const res = await getHistory(credential._id, 20)
      setRows(res?.data || [])
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credential?._id])

  useEffect(() => {
    load()
  }, [load])

  const Actions = (
    <ButtonStartIcon onClick={onClose} color='primary' variant='outlined' startIcon={['fal', 'times']}>
      Close
    </ButtonStartIcon>
  )

  return (
    <Dialog
      id='third-party-access-history'
      show={show}
      title={`Sync history — ${credential.label || credential.schoolSlug || ''}`}
      maxWidth='lg'
      onClose={onClose}
      actions={Actions}
    >
      <div className='flex justify-end pb-2'>
        <ButtonStartIcon
          onClick={load}
          color='primary'
          variant='outlined'
          loading={loading}
          startIcon={['fas', 'sync']}
        >
          Refresh
        </ButtonStartIcon>
      </div>

      {loading && rows.length === 0 ? (
        <Skeleton variant='rectangular' height={160} />
      ) : rows.length === 0 ? (
        <Typography
          variant='inherit'
          component='p'
          className='text-muted-foreground opacity-50 py-4 text-center'
        >
          No sync runs yet. Use the Sync button to pull data.
        </Typography>
      ) : (
        <div className='overflow-x-auto'>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell>
                <TableCell>Finished</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align='right'>Fetched</TableCell>
                <TableCell align='right'>Upserted</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Scales</TableCell>
                <TableCell>By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((run) => {
                const status = STATUS_CHIP[run.status] || {
                  label: run.status,
                  color: 'default'
                }
                const period =
                  run.period?.from || run.period?.to
                    ? `${run.period?.from || '?'} → ${run.period?.to || '?'}`
                    : '—'
                return (
                  <TableRow key={run._id}>
                    <TableCell>{formatDate(run.startedAt)}</TableCell>
                    <TableCell>{formatDate(run.finishedAt)}</TableCell>
                    <TableCell sx={{ verticalAlign: 'top', maxWidth: 360 }}>
                      <Chip label={status.label} color={status.color} size='small' />
                      {run.status === 'error' && run.error && (
                        <Typography
                          variant='caption'
                          color='error'
                          sx={{ display: 'block', mt: 0.5, lineHeight: 1.35, overflowWrap: 'anywhere' }}
                        >
                          {run.error}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align='right'>{run.recordsFetched ?? '—'}</TableCell>
                    <TableCell align='right'>{run.recordsUpserted ?? '—'}</TableCell>
                    <TableCell>{period}</TableCell>
                    <TableCell>{(run.scales || []).join(', ') || '—'}</TableCell>
                    <TableCell>{triggeredByLabel(run)}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </Dialog>
  )
}

export default HistoryDialog
